import { useParams, useNavigate } from "react-router-dom";
import { PropertyReport } from "@/components/PropertyReport";
import { getSampleReportBySlug, sampleReports } from "@/data/sampleReports";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Home, Eye, ChevronLeft, ChevronRight, Search, MapPin } from "lucide-react";
import { usePageMeta, createReportSchema, createPropertyPlaceSchema } from "@/hooks/usePageMeta";

const SampleReportDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const sampleReport = slug ? getSampleReportBySlug(slug) : undefined;
  
  // Find current index and calculate prev/next
  const currentIndex = sampleReports.findIndex(r => r.slug === slug);
  const prevReport = currentIndex > 0 ? sampleReports[currentIndex - 1] : null;
  const nextReport = currentIndex < sampleReports.length - 1 ? sampleReports[currentIndex + 1] : null;

  // Add page-specific meta tags and schema markup for SEO
  usePageMeta({
    title: sampleReport 
      ? `${sampleReport.address} - Property Report | Let's Be Real`
      : "Report Not Found | Let's Be Real",
    description: sampleReport?.shortDescription || "Sample property report not found",
    canonical: `https://letsbereal.com.au/sample-reports/${slug}`,
    ogImage: "https://letsbereal.com.au/lbr.png",
    ogType: "article",
    twitterCard: "summary_large_image",
    keywords: sampleReport ? `${sampleReport.address}, property report, census data, neighborhood analysis, australian property, ${sampleReport.address.split(',')[1]?.trim()}` : "property report",
    author: "Let's Be Real",
    schema: sampleReport ? {
      "@context": "https://schema.org",
      "@graph": [
        createReportSchema({
          title: `Property Report: ${sampleReport.address}`,
          description: sampleReport.shortDescription,
          url: `https://letsbereal.com.au/sample-reports/${slug}`,
          datePublished: "2025-10-22",
          dateModified: "2025-10-22",
        }),
        createPropertyPlaceSchema({
          address: sampleReport.address,
          description: sampleReport.shortDescription,
          url: `https://letsbereal.com.au/sample-reports/${slug}`,
        }),
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://letsbereal.com.au/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Sample Reports",
              "item": "https://letsbereal.com.au/sample-reports"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": sampleReport.address,
              "item": `https://letsbereal.com.au/sample-reports/${slug}`
            }
          ]
        }
      ]
    } : undefined
  });

  if (!sampleReport) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold">Report Not Found</h1>
            <p className="text-muted-foreground">
              The sample report you're looking for doesn't exist.
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate("/sample-reports")} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                View All Samples
              </Button>
              <Button onClick={() => navigate("/")}>
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
            </div>
          </div>
      </div>
    );
  }

  const handleReset = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sample Report Indicator Banner */}
      <div className="bg-primary/5 border-b border-primary/20">
        <div className="container mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-start">
            <Badge variant="outline" className="border-primary/50 bg-primary/10">
              <Eye className="h-3 w-3 mr-1" /> Sample Report
            </Badge>
            <span className="text-sm text-muted-foreground text-center sm:text-left">
              Pre-generated example using real data
            </span>
          </div>
          <Button size="sm" variant="default" onClick={() => navigate("/")} className="gap-2">
            <Search className="h-4 w-4" />
            Search Your Address
          </Button>
        </div>
      </div>

      {/* Property Report Component */}
      <PropertyReport 
        report={sampleReport.report}
        quickFacts={sampleReport.quickFacts}
        onReset={handleReset}
        isSampleReport={true}
      />

      {/* Sample Navigation */}
      <div className="container mx-auto px-4 py-8 border-t">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-6">
            <Button 
              variant="ghost" 
              onClick={() => prevReport && navigate(`/sample-reports/${prevReport.slug}`)}
              disabled={!prevReport}
              className="gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{prevReport?.shortTitle || "Previous"}</span>
            </Button>
            
            <div className="flex flex-wrap gap-2 justify-center">
              {sampleReports.map((sample) => (
                <Badge 
                  key={sample.slug} 
                  variant={sample.slug === slug ? "default" : "outline"}
                  className="cursor-pointer hover:bg-primary/20 transition-colors"
                  onClick={() => navigate(`/sample-reports/${sample.slug}`)}
                >
                  {sample.shortTitle}
                </Badge>
              ))}
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => nextReport && navigate(`/sample-reports/${nextReport.slug}`)}
              disabled={!nextReport}
              className="gap-2"
            >
              <span className="hidden sm:inline">{nextReport?.shortTitle || "Next"}</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* CTA Section */}
          <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <h2 className="text-xl sm:text-2xl font-bold mb-2">Ready to analyze your property?</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Get the same honest, data-driven analysis for any Australian address
              </p>
              <Button 
                size="lg" 
                onClick={() => navigate("/")}
                className="shadow-lg gap-2"
              >
                <MapPin className="h-4 w-4" />
                Search Your Property
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                12,847+ reports generated • Free forever
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SampleReportDetail;

